import React, { useState, useEffect } from 'react';
import { ExternalLink, Handshake } from 'lucide-react';
import { PartnersMap } from './PartnersMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Partners = () => {
  const [partnerCategories, setPartnerCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/partners`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch partners');
        }
        
        const partners = await response.json();
        
        // Group partners by category
        const grouped = partners.reduce((acc, partner) => {
          const existingCategory = acc.find(cat => cat.category === partner.category);
          
          if (existingCategory) {
            existingCategory.partners.push(partner);
          } else {
            acc.push({
              category: partner.category,
              partners: [partner]
            });
          }
          
          return acc;
        }, []);
        
        // Sort categories - put "Overseas Pet Travel Agents" last
        grouped.sort((a, b) => {
          if (a.category === "Overseas Pet Travel Agents") return 1;
          if (b.category === "Overseas Pet Travel Agents") return -1;
          return 0;
        });
        
        setPartnerCategories(grouped);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching partners:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPartners();
  }, []);

  // Function to get country flag emoji and name
  const getCountryInfo = (location) => {
    const countryMap = {
      "Costa Rica": { flag: "🇨🇷", name: "Costa Rica" },
      "United Arab Emirates": { flag: "🇦🇪", name: "UAE" },
      "Fort Lauderdale, Florida, USA": { flag: "🇺🇸", name: "United States" },
      "Panama": { flag: "🇵🇦", name: "Panama" },
      "Germany & Switzerland": { flag: "🇩🇪🇨🇭", name: "Germany & Switzerland" },
      "Ireland": { flag: "🇮🇪", name: "Ireland" },
      "South Africa": { flag: "🇿🇦", name: "South Africa" },
      "Thailand": { flag: "🇹🇭", name: "Thailand" },
      "Wellington, New Zealand": { flag: "🇳🇿", name: "New Zealand" },
      "Cainta, Rizal, Philippines": { flag: "🇵🇭", name: "Philippines" },
      "Santo Domingo, Dominican Republic": { flag: "🇩🇴", name: "Dominican Republic" },
      "United Kingdom": { flag: "🇬🇧", name: "United Kingdom" }
    };
    return countryMap[location] || null;
  };

  if (loading) {
    return (
      <section id="partners" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <Handshake className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Loading Partners...</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="partners" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-red-500">Error loading partners: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="partners" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-6">
              <Handshake className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Trusted Partnerships</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Collaborative Partners
            </h2>
            <p className="text-base sm:text-lg text-foreground/70 max-w-2xl mx-auto">
              We work with trusted partners to provide comprehensive pet travel services and ensure the highest standards of care for your furry companions.
            </p>
          </div>

          {/* Global Partner Map */}
          <PartnersMap />

          {/* Partners by Category */}
          <div className="space-y-12">
            {partnerCategories.map((categoryGroup, categoryIndex) => (
              <div key={categoryIndex}>
                {/* Category Header */}
                <div className="mb-6">
                  <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                    {categoryGroup.category}
                  </h3>
                  <div className="h-1 w-20 bg-primary rounded-full"></div>
                </div>

                {/* Partners in this category */}
                <div className="grid grid-cols-1 gap-6">
                  {categoryGroup.partners.map((partner, partnerIndex) => (
                    <div 
                      key={partner.id || partnerIndex} 
                      className="bg-white rounded-2xl shadow-lg border border-border p-8 md:p-10 hover:shadow-xl transition-all"
                    >
                      <div className="flex flex-col md:flex-row gap-8">
                        {/* Partner Logo/Icon */}
                        <div className="flex-shrink-0">
                          {partner.logo ? (
                            <div className="relative">
                              <div className="w-32 h-32 rounded-xl bg-white flex items-center justify-center p-4 border border-border">
                                <img 
                                  src={partner.logo} 
                                  alt={`${partner.name} logo`}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                              {/* Country Flag Badge for Overseas Partners */}
                              {categoryGroup.category === "Overseas Pet Travel Agents" && partner.location && getCountryInfo(partner.location) && (
                                <div className="mt-2 flex items-center justify-center gap-2 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg px-3 py-1.5">
                                  <span className="text-2xl emoji-flag">{getCountryInfo(partner.location).flag}</span>
                                  <span className="text-xs font-semibold text-primary">{getCountryInfo(partner.location).name}</span>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="relative">
                              <div className="w-24 h-24 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Handshake className="w-12 h-12 text-primary" />
                              </div>
                              {/* Country Flag Badge for Overseas Partners without logo */}
                              {categoryGroup.category === "Overseas Pet Travel Agents" && partner.location && getCountryInfo(partner.location) && (
                                <div className="mt-2 flex items-center justify-center gap-2 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg px-3 py-1.5">
                                  <span className="text-2xl emoji-flag">{getCountryInfo(partner.location).flag}</span>
                                  <span className="text-xs font-semibold text-primary">{getCountryInfo(partner.location).name}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Partner Info */}
                        <div className="flex-1">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                            <div>
                              <h3 className="text-2xl font-bold text-foreground mb-2">
                                {partner.name}
                                {partner.special && (
                                  <span className="ml-3 text-xs bg-primary text-white px-3 py-1 rounded-full font-normal">
                                    {partner.special}
                                  </span>
                                )}
                              </h3>
                              <p className="text-foreground/70 leading-relaxed">
                                {partner.description}
                              </p>
                              {partner.contact && (
                                <p className="text-foreground/60 text-sm mt-2 whitespace-pre-line">
                                  {partner.contact}
                                </p>
                              )}
                            </div>
                            
                            {partner.website && (
                              <a
                                href={partner.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold transition-colors group flex-shrink-0"
                              >
                                Visit Website
                                <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                              </a>
                            )}
                          </div>

                          {/* Services Provided */}
                          {partner.services && partner.services.length > 0 && (
                            <div className="mt-6">
                              <h4 className="text-sm font-semibold text-foreground/60 uppercase tracking-wide mb-3">
                                Services Provided
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {partner.services.map((service, idx) => (
                                  <div key={idx} className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                                    <span className="text-foreground/70 text-sm">{service}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Note */}
          <div className="mt-12 text-center">
            <p className="text-foreground/60 text-sm">
              Interested in partnering with Pawsome Ventures?{' '}
              <button
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-primary hover:text-primary/80 font-semibold underline"
              >
                Get in touch
              </button>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
